import React from 'react';
import { StockMonitorTask } from '../../services/stockMonitorService';
import { getTimeFrameName } from '../../utils/timeFrameUtils';
import { formatPrice, getSignalStatus } from './utils';
import { BarInterval, ORDER_SIDE } from '@/services/enums';

interface MonitorCardProps {
  monitor: StockMonitorTask;
  isStopping: boolean;
  isStarting: boolean;
  onStop: (monitorId: string, e: React.MouseEvent) => void;
  onStart: (monitorId: string, symbol: string, bar: BarInterval, e: React.MouseEvent) => void;
  onViewPrompt: (monitor: StockMonitorTask, e: React.MouseEvent) => void;
  onViewReport: (monitor: StockMonitorTask, e: React.MouseEvent) => void;
  onClick: (monitor: StockMonitorTask) => void;
}

const MonitorCard: React.FC<MonitorCardProps> = ({
  monitor,
  isStopping,
  isStarting,
  onStop,
  onStart,
  onViewPrompt,
  onViewReport,
  onClick,
}) => {
  const signalStatus = getSignalStatus(monitor.signals);

  // 解析管道状态
  const getPipelineStatusLabel = (statusReason?: string) => {
    if (!statusReason) return null;
    if (statusReason.startsWith('Pipeline Status: ')) {
      const status = statusReason.replace('Pipeline Status: ', '');
      switch (status) {
        case 'STARTING':
          return { label: '启动中', color: 'bg-blue-500/20 text-blue-300' };
        case 'SELECTING':
          return { label: '选股中', color: 'bg-cyan-500/20 text-cyan-300' };
        case 'FILTERING':
          return { label: '过滤中', color: 'bg-teal-500/20 text-teal-300' };
        case 'INITIALIZING_ASSET':
          return { label: '初始化中', color: 'bg-blue-500/20 text-blue-300' };
        case 'PROMPT':
          return { label: '准备提示词', color: 'bg-yellow-500/20 text-yellow-300' };
        case 'ANALYZING':
          return { label: 'AI分析中', color: 'bg-purple-500/20 text-purple-300' };
        case 'EXECUTING':
          return { label: '推送结果', color: 'bg-indigo-500/20 text-indigo-300' };
        case 'WAITING':
          return { label: '等待中', color: 'bg-orange-500/20 text-orange-300' };
        case 'COMPLETED':
          return { label: '调研完成', color: 'bg-green-500/20 text-green-300' };
        case 'FAILED':
          return { label: '分析失败', color: 'bg-red-500/20 text-red-300' };
        default:
          return { label: status, color: 'bg-gray-500/20 text-gray-300' };
      }
    }
    return null;
  };

  const pipelineStatus = getPipelineStatusLabel(monitor.statusReason);

  return (
    <div
      className={`${
        monitor.status === 'running' ? 'bg-white/5 border-white/20' : 'bg-white/3 border-white/10'
      } border rounded-xl p-4 text-white/90 mb-2 cursor-pointer hover:bg-white/10 transition-colors`}
      onClick={() => onClick(monitor)}
    >
      {/* 第一行：股票代码和时间粒度 */}
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="flex flex-col">
            <h3 className="font-bold text-white text-lg truncate">{monitor.name || monitor.symbol.toUpperCase()}</h3>
            <span className="text-white/40 text-[10px] font-mono">{monitor.strategyId}</span>
          </div>
          <span className="px-2 py-1 bg-white/10 border border-white/20 rounded text-xs text-white/70 flex-shrink-0">
            {getTimeFrameName(monitor.bar)}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {monitor.enableAutoTrading && monitor.status === 'running' && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/50">
              自动交易
            </span>
          )}
          {monitor.status === 'running' && (
            <span className="px-2 py-1 rounded text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/50">
              运行中
            </span>
          )}
          {pipelineStatus && (
            <span
              className={`px-2 py-1 rounded text-xs font-semibold border ${pipelineStatus.color} border-current/30`}
            >
              {pipelineStatus.label}
            </span>
          )}
        </div>
      </div>

      {/* 第二行：当前价格和指标 */}
      <div className="mb-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">当前价格:</span>
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold">{formatPrice(monitor.price || monitor.currentData?.price)}</span>
            {(monitor.change !== undefined || monitor.currentData?.change !== undefined) && (
              <span
                className={`text-xs font-medium ${
                  (monitor.change ?? monitor.currentData?.change ?? 0) >= 0 ? 'text-red-400' : 'text-green-400'
                }`}
              >
                {(monitor.change ?? monitor.currentData?.change ?? 0).toFixed(2)}%
              </span>
            )}
          </div>
        </div>
        {monitor.currentData && (
          <>
            {monitor.currentData.rsi14 !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">RSI(14):</span>
                <span className="text-white">{monitor.currentData.rsi14.toFixed(2)}</span>
              </div>
            )}
            {monitor.currentData.macd !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-white/60 text-sm">MACD:</span>
                <span className="text-white">{monitor.currentData.macd.toFixed(4)}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* 第三行：时间范围和重新生成状态 */}
      <div className="mb-3 space-y-2">
        {monitor.ruleStartTime && monitor.ruleEndTime && (
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">时间范围:</span>
            <span className="text-white text-xs">
              {new Date(monitor.ruleStartTime * 1000).toLocaleString('zh-CN', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              -{' '}
              {new Date(monitor.ruleEndTime * 1000).toLocaleString('zh-CN', {
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
        )}
        {monitor.systemPromptId && (
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">提示词ID:</span>
            <span className="text-white text-xs font-mono">{monitor.systemPromptId}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">重新生成状态:</span>
          <span
            className={`text-xs px-2 py-1 rounded ${
              monitor.regenerateStatus === 'idle'
                ? 'bg-gray-500/20 text-gray-300'
                : monitor.regenerateStatus === 'generating'
                  ? 'bg-yellow-500/20 text-yellow-300'
                  : monitor.regenerateStatus === 'analyzing'
                    ? 'bg-blue-500/20 text-blue-300'
                    : monitor.regenerateStatus === 'completed'
                      ? 'bg-green-500/20 text-green-300'
                      : monitor.regenerateStatus === 'failed'
                        ? 'bg-red-500/20 text-red-300'
                        : 'bg-gray-500/20 text-gray-300'
            }`}
          >
            {monitor.regenerateStatus === 'idle'
              ? '空闲'
              : monitor.regenerateStatus === 'generating'
                ? '生成中'
                : monitor.regenerateStatus === 'analyzing'
                  ? '分析中'
                  : monitor.regenerateStatus === 'completed'
                    ? '已完成'
                    : monitor.regenerateStatus === 'failed'
                      ? '失败'
                      : '未知'}
          </span>
        </div>
        {monitor.regenerateStatus === 'failed' && monitor.regenerateFailureReason && (
          <div className="text-xs text-red-300 bg-red-500/10 p-2 rounded border border-red-500/30 break-all">
            失败原因: {monitor.regenerateFailureReason}
          </div>
        )}
      </div>

      {/* 第四行：信号状态 */}
      <div className="mb-3">
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">监控信号:</span>
          <span className="text-white">
            {signalStatus.triggeredCount} / {signalStatus.totalCount} 已触发
          </span>
        </div>
        {signalStatus.triggeredCount > 0 && (
          <div className="mt-2 space-y-1">
            {monitor.signals
              .filter((s) => s.triggered)
              .map((signal, idx) => (
                <div
                  key={idx}
                  className={`px-2 py-1 rounded text-xs ${
                    signal.type === ORDER_SIDE.BUY
                      ? 'bg-green-500/20 text-green-300 border border-green-500/50'
                      : 'bg-red-500/20 text-red-300 border border-red-500/50'
                  }`}
                >
                  {signal.type === ORDER_SIDE.BUY ? '🟢' : '🔴'} {signal.description}
                </div>
              ))}
          </div>
        )}
      </div>

      {/* AI 评分与报告摘要 */}
      <div className="mb-3 space-y-2">
        {monitor.analysisScore !== undefined && (
          <div className="p-2 bg-white/5 rounded border border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-white/60 text-xs">AI 调研评分:</span>
              <span
                className={`text-sm font-bold ${
                  monitor.analysisScore >= 90
                    ? 'text-green-400'
                    : monitor.analysisScore >= 70
                      ? 'text-blue-400'
                      : 'text-gray-400'
                }`}
              >
                {monitor.analysisScore} 分
              </span>
            </div>
          </div>
        )}

        {/* WFO 寻优结果 */}
        {(monitor.tradingParams?.wfoEfficiency !== undefined ||
          monitor.tradingParams?.sensitivityScore !== undefined ||
          monitor.tradingParams?.mcStabilityScore !== undefined) && (
          <div className="p-2 bg-blue-500/5 rounded border border-blue-500/10 space-y-2">
            {monitor.tradingParams?.wfoEfficiency !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-blue-300/60 text-xs flex items-center gap-1">
                  <span>🧪</span> WFO 寻优效率:
                </span>
                <span
                  className={`text-sm font-bold ${
                    monitor.tradingParams.wfoEfficiency >= 0.7
                      ? 'text-green-400'
                      : monitor.tradingParams.wfoEfficiency >= 0.5
                        ? 'text-blue-400'
                        : 'text-yellow-400'
                  }`}
                >
                  {(monitor.tradingParams.wfoEfficiency * 100).toFixed(1)}%
                </span>
              </div>
            )}

            {monitor.tradingParams?.sensitivityScore !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-blue-300/60 text-xs flex items-center gap-1">
                  <span>🛡️</span> 参数稳定性:
                </span>
                <span
                  className={`text-sm font-bold ${
                    monitor.tradingParams.sensitivityScore >= 0.8
                      ? 'text-green-400'
                      : monitor.tradingParams.sensitivityScore >= 0.6
                        ? 'text-blue-400'
                        : 'text-yellow-400'
                  }`}
                >
                  {(monitor.tradingParams.sensitivityScore * 100).toFixed(1)}%
                </span>
              </div>
            )}

            {monitor.tradingParams?.mcStabilityScore !== undefined && (
              <div className="flex items-center justify-between">
                <span className="text-blue-300/60 text-xs flex items-center gap-1">
                  <span>🎲</span> 蒙特卡洛稳定性:
                </span>
                <span
                  className={`text-sm font-bold ${
                    monitor.tradingParams.mcStabilityScore >= 0.9
                      ? 'text-green-400'
                      : monitor.tradingParams.mcStabilityScore >= 0.7
                        ? 'text-blue-400'
                        : 'text-yellow-400'
                  }`}
                >
                  {(monitor.tradingParams.mcStabilityScore * 100).toFixed(1)}%
                </span>
              </div>
            )}

            {monitor.tradingParams.oosAnnualReturn !== undefined && (
              <div className="flex justify-between text-[10px] text-white/40 border-t border-white/5 pt-1">
                <span>OOS 年化: {(monitor.tradingParams.oosAnnualReturn * 100).toFixed(1)}%</span>
                <span>最大回撤: {(monitor.tradingParams.oosMaxDrawdown * 100).toFixed(1)}%</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 底部按钮 */}
      <div className="flex items-center justify-end gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={(e) => onViewPrompt(monitor, e)}
          className="px-3 py-1.5 rounded text-xs bg-white/5 hover:bg-white/10 border border-white/10 transition-colors text-white/70"
        >
          查看提示词
        </button>
        {(monitor.analysisReportHash || monitor.analysisDescription) && (
          <button
            onClick={(e) => onViewReport(monitor, e)}
            className="px-3 py-1.5 rounded text-xs bg-blue-500/20 text-blue-300 border border-blue-500/50 hover:bg-blue-500/30 transition-colors"
          >
            查看报告
          </button>
        )}
        {monitor.status === 'running' ? (
          <button
            onClick={(e) => onStop(monitor.monitorId, e)}
            disabled={isStopping}
            className="px-3 py-1.5 rounded text-xs bg-red-500/20 text-red-300 border border-red-500/50 hover:bg-red-500/30 transition-colors disabled:opacity-50"
          >
            {isStopping ? '停止中...' : '停止监控'}
          </button>
        ) : (
          <button
            onClick={(e) => onStart(monitor.monitorId, monitor.symbol, monitor.bar, e)}
            disabled={isStarting}
            className="px-3 py-1.5 rounded text-xs bg-green-500/20 text-green-300 border border-green-500/50 hover:bg-green-500/30 transition-colors disabled:opacity-50"
          >
            {isStarting ? '启动中...' : '启动监控'}
          </button>
        )}
      </div>
    </div>
  );
};

export default MonitorCard;
